import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { amendmentDrawerSections } from '@common/constants';
import {
  useCreateAmendment,
  useCreateCancellationAmendment,
  useGetAmendmentSubtypes,
  useGetSystemReferenceDate
} from '@common/slices';
import {
  AmendmentForm,
  AmendmentLookups,
  IBaseDrawerProps
} from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { useAppSelector } from '@core/redux/hooks';
import { RootState } from '@core/redux/store';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getLineCategory } from '@utils/helper-utils';
import { LineCategory } from 'src/enums';

interface Props extends IBaseDrawerProps {
  policyId: string;
  policyLineCode?: string;
  policyData?: {
    policyEffectiveDate?: string;
    policyExpiryDate?: string;
  };
}

const NewAmendmentDrawer: React.FC<Props> = ({
  policyId,
  policyLineCode,
  policyData,
  onSuccess
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const toMidnightUtcIso = (value: string | Date): string => {
    const d = new Date(value);
    const utcMidnight = new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
    );

    return utcMidnight.toISOString();
  };

  const { data: systemRefResp } = useGetSystemReferenceDate.useQuery();

  const systemReferenceDateStr =
    systemRefResp?.core?.getSystemReferenceDate ?? new Date().toISOString();

  const systemReferenceDate = new Date(systemReferenceDateStr);
  const today = new Date();

  const lineCategory = getLineCategory(policyLineCode);
  const isRedBridgeTravel = lineCategory === LineCategory.RedBridgeTravel;

  const { data: subtypesResp } = useGetAmendmentSubtypes.useQuery();
  const [lookups, setLookups] = useState<AmendmentLookups | null>(null);

  const [createAmendment] = useCreateAmendment();
  const [createCancellationAmendment] = useCreateCancellationAmendment();

  const allowPolicyEndorsement = !!useAppSelector(
    (s) => s.user?.info?.businessUser_AllowEndorsement
  );
  const allowPolicyCancellation = !!useAppSelector(
    (s) => s.user?.info?.businessUser_AllowPolicyCancellation
  );

  // Determine default values based on line code
  const getDefaultAmendmentType = () => {
    return isRedBridgeTravel ? 'POLICY_CANCELLATION' : '';
  };

  const getDefaultEffectiveDate = () => {
    if (isRedBridgeTravel && policyData?.policyEffectiveDate) {
      return policyData.policyEffectiveDate;
    }
    return systemReferenceDate.toISOString();
  };

  const defaultIssue = (
    today > systemReferenceDate ? today : systemReferenceDate
  ).toISOString();

  const formik = useFormik<AmendmentForm>({
    initialValues: {
      amendmentType: getDefaultAmendmentType(),
      amendmentSubtypeId: '',
      amendmentIssueDate: defaultIssue,
      amendmentEffectiveDate: getDefaultEffectiveDate(),
      amendmentReason: ''
    },
    validationSchema: Yup.object({
      amendmentType: Yup.mixed<'POLICY_CANCELLATION' | 'POLICY_ENDORSEMENT'>()
        .oneOf(['POLICY_CANCELLATION', 'POLICY_ENDORSEMENT'])
        .required('Required'),
      amendmentSubtypeId: Yup.string().when('amendmentType', {
        is: 'POLICY_ENDORSEMENT',
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.notRequired()
      }),
      amendmentIssueDate: Yup.date().required('Required'),
      amendmentEffectiveDate: Yup.date().required('Required'),
      amendmentReason: Yup.string().when('amendmentType', {
        is: 'POLICY_CANCELLATION',
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.notRequired()
      })
    }),
    onSubmit: async (values) => {
      try {
        const isEndorsement = values.amendmentType === 'POLICY_ENDORSEMENT';

        const variables = {
          input: {
            policyId,
            amendmentType: values.amendmentType,
            amendmentSubtype: isEndorsement
              ? (values.amendmentSubtypeId ?? '')
              : null,
            amendmentIssueDate: new Date(
              values.amendmentIssueDate
            ).toISOString(),
            amendmentEffectiveDate: toMidnightUtcIso(
              values.amendmentEffectiveDate
            ),
            ...(values.amendmentReason && {
              amendmentReason: values.amendmentReason
            })
          }
        };
        let amendmentId;
        if (isRedBridgeTravel) {
          const response = await createCancellationAmendment(variables);
          amendmentId = response.data?.production?.createCancellationAmendment;
        } else {
          const response = await createAmendment(variables);
          amendmentId = response.data?.production?.createAmendment;
        }

        if (!amendmentId) {
          throw new Error('Failed to create cancellation amendment');
        }
        onSuccess?.(
          `/production/amendments/${encodeURIComponent(amendmentId)}`
        );
      } catch (error) {
        console.error(error);
        toast.error(<ToastErrorMessage>{error.message}</ToastErrorMessage>);
      }
    }
  });

  useEffect(() => {
    const allAmendmentTypes: Record<string, string> = {
      POLICY_CANCELLATION: 'Policy Cancellation',
      POLICY_ENDORSEMENT: 'Policy Endorsement'
    };

    const amendmentTypes: Record<string, string> = {};

    // For line code 52, only show Policy Cancellation when user has permission
    if (isRedBridgeTravel) {
      if (allowPolicyCancellation) {
        amendmentTypes.POLICY_CANCELLATION =
          allAmendmentTypes.POLICY_CANCELLATION;
      }
    } else {
      // For other line codes, show based on permissions
      if (allowPolicyCancellation) {
        amendmentTypes.POLICY_CANCELLATION =
          allAmendmentTypes.POLICY_CANCELLATION;
      }

      if (allowPolicyEndorsement) {
        amendmentTypes.POLICY_ENDORSEMENT =
          allAmendmentTypes.POLICY_ENDORSEMENT;
      }
    }

    const amendmentSubtypes: Record<string, string> = (
      subtypesResp?.production?.getAmendmentSubTypes ?? []
    ).reduce<Record<string, string>>((acc, it) => {
      acc[it.id] = it.title;
      return acc;
    }, {});

    const allowedKeys = Object.keys(amendmentTypes);
    if (
      formik.values.amendmentType &&
      !allowedKeys.includes(formik.values.amendmentType)
    ) {
      formik.setFieldValue('amendmentType', getDefaultAmendmentType());
      formik.setFieldValue('amendmentSubtypeId', '');
    }

    setLookups({ amendmentTypes, amendmentSubtypes });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subtypesResp,
    allowPolicyCancellation,
    allowPolicyEndorsement,
    policyLineCode,
    isRedBridgeTravel
  ]);

  useEffect(() => {
    const newDefaultIssue = (
      today > systemReferenceDate ? today : systemReferenceDate
    ).toISOString();

    const newDefaultEffective = getDefaultEffectiveDate();

    if (formik.values.amendmentIssueDate !== newDefaultIssue) {
      formik.setFieldValue('amendmentIssueDate', newDefaultIssue, false);
    }
    if (formik.values.amendmentEffectiveDate !== newDefaultEffective) {
      formik.setFieldValue(
        'amendmentEffectiveDate',
        newDefaultEffective,
        false
      );
    }
  }, []);

  const handleChange = useCallback(
    (name: string, value: string | number | boolean) => {
      if (
        name === 'amendmentIssueDate' ||
        (name === 'amendmentEffectiveDate' &&
          (!isRedBridgeTravel ||
            formik.values.amendmentType !== 'POLICY_CANCELLATION'))
      ) {
        return;
      }

      formik.setFieldValue(name, value);
      formik.setFieldTouched(name, true);

      if (name === 'amendmentType' && value !== 'POLICY_ENDORSEMENT') {
        formik.setFieldValue('amendmentSubtypeId', '');
      }
    },
    [formik, isRedBridgeTravel]
  );

  const sections = useMemo(
    () =>
      lookups
        ? amendmentDrawerSections(formik as any, lookups, {
            policyLineCode,
            policyEffectiveDate: policyData?.policyEffectiveDate,
            policyExpiryDate: policyData?.policyExpiryDate
          })
        : [],
    [
      lookups,
      formik.values,
      formik.errors,
      formik.touched,
      policyLineCode,
      policyData?.policyEffectiveDate,
      policyData?.policyExpiryDate
    ]
  );

  if (!lookups) {
    return <></>;
  }

  return (
    <>
      {<div style={{ marginBottom: 12 }}></div>}
      <DynamicFormWidget
        onChange={handleChange}
        sections={sections}
        onSubmit={() => formik.handleSubmit()}
        submitButtonState={submitButtonState}
      />
    </>
  );
};

export { NewAmendmentDrawer };
